import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchOrderApiResult } from '@/api/order';
import { fetchSearchQuota, fetchSearchResult } from '@/api/search';
import { getToken } from '@/lib/auth-storage';
import { openSaleSmartlyChat } from '@/lib/salesmartly';
import {
  SEARCH_RESULT_PAGE_SIZE,
  collectSearchResultFieldKeys,
  formatSearchFieldValue,
  getSearchResultPageCount,
  paginateSearchRows,
  parseSearchApiResponse,
  type SearchResultRow,
} from '@/utils/search-result';

type LocationState = {
  failureReason?: string;
  pointsBalance?: number;
  responseJson?: string;
  keyword?: string;
  productName?: string;
  searchOrderId?: number;
  status?: string;
};

const SEARCH_FAILED_STATUS = '3';
const SEARCH_FAILURE_REASON = '没有找到相关信息，请尝试重新查询或等待数据更新';

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const sid = Number(searchParams.get('sid') || 0);
  const orderNo = searchParams.get('oid') || '';
  const keywordFromUrl = searchParams.get('q') || '';
  const page = Math.max(1, Number(searchParams.get('page') || 1) || 1);

  const [responseJson, setResponseJson] = useState<string | undefined>();
  const [keyword, setKeyword] = useState(keywordFromUrl);
  const [productName, setProductName] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [stateSearchOrderId, setStateSearchOrderId] = useState<number | undefined>();
  const [pointsBalance, setPointsBalance] = useState<number | undefined>();
  const [failureReason, setFailureReason] = useState(SEARCH_FAILURE_REASON);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      const redirect = `${location.pathname}${location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
      return;
    }

    const state = location.state as LocationState | null;
    if (state?.responseJson || state?.status) {
      setResponseJson(state.responseJson);
      setKeyword(state.keyword || keywordFromUrl);
      setProductName(state.productName || '');
      setSearchStatus(state.status || '');
      setStateSearchOrderId(state.searchOrderId);
      setPointsBalance(state.pointsBalance);
      setFailureReason(state.failureReason || SEARCH_FAILURE_REASON);
      setLoadError('');
      setLoading(false);
      return;
    }

    if (orderNo) {
      let cancelled = false;
      setLoading(true);
      setLoadError('');
      fetchOrderApiResult(orderNo)
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 0 || !res.data) {
            setLoadError(res.msg || '加载失败');
            return;
          }
          setResponseJson(res.data.responseJson);
          setProductName(res.data.productName || '');
          setSearchStatus('');
          setStateSearchOrderId(undefined);
          setPointsBalance(undefined);
          setFailureReason(SEARCH_FAILURE_REASON);
        })
        .catch(() => {
          if (!cancelled) setLoadError('网络异常');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (!sid || sid <= 0) {
      setLoadError('缺少搜索记录或订单号');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchSearchResult(sid)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 0 || !res.data) {
          setLoadError(res.msg || '加载失败');
          return;
        }
        setResponseJson(res.data.responseJson);
        setKeyword(res.data.keyword || keywordFromUrl);
        setSearchStatus(res.data.status || '');
        setStateSearchOrderId(res.data.searchOrderId || sid);
        setPointsBalance(res.data.pointsBalance);
        setFailureReason(res.data.failureReason || SEARCH_FAILURE_REASON);
      })
      .catch(() => {
        if (!cancelled) setLoadError('网络异常');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sid, orderNo, keywordFromUrl, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (searchStatus !== SEARCH_FAILED_STATUS || pointsBalance != null || !getToken()) {
      return;
    }

    let cancelled = false;
    fetchSearchQuota()
      .then((res) => {
        if (!cancelled && res.code === 0 && res.data) {
          setPointsBalance(res.data.pointsBalance);
        }
      })
      .catch(() => {
        // The failure page remains useful even if the latest balance cannot be refreshed.
      });

    return () => {
      cancelled = true;
    };
  }, [pointsBalance, searchStatus]);

  const parsed = useMemo(() => parseSearchApiResponse(responseJson), [responseJson]);
  const isSearchFailed = searchStatus === SEARCH_FAILED_STATUS;

  const pageCount = getSearchResultPageCount(parsed.rows.length, SEARCH_RESULT_PAGE_SIZE);
  const safePage = pageCount > 0 ? Math.min(page, pageCount) : 1;

  const pageRows = useMemo(
    () => paginateSearchRows(parsed.rows, safePage, SEARCH_RESULT_PAGE_SIZE),
    [parsed.rows, safePage],
  );

  const fieldKeys = useMemo(() => collectSearchResultFieldKeys(pageRows), [pageRows]);

  const rangeStart = parsed.rows.length === 0 ? 0 : (safePage - 1) * SEARCH_RESULT_PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * SEARCH_RESULT_PAGE_SIZE, parsed.rows.length);

  const goToPage = (nextPage: number) => {
    const p = Math.max(1, Math.min(nextPage, pageCount || 1));
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    if (sid > 0) next.set('sid', String(sid));
    if (orderNo) next.set('oid', orderNo);
    if (keyword) next.set('q', keyword);
    setSearchParams(next);
  };

  useEffect(() => {
    if (pageCount > 0 && page > pageCount) {
      goToPage(pageCount);
    }
  }, [page, pageCount]);

  const showError = !isSearchFailed
    && (loadError || (!loading && !parsed.ok) || (!loading && parsed.ok && parsed.rows.length === 0));
  const errorMessage = loadError || parsed.message || '未查询到匹配数据';
  const currentSearchOrderId = sid > 0 ? sid : stateSearchOrderId;
  const handleContactSupport = () => {
    openSaleSmartlyChat({
      message: `首页搜索查询失败咨询\n查询内容：${keyword || '-'}\n查询ID：${currentSearchOrderId || '-'}`,
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/" className="hover:text-gray-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">搜索结果</span>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {isSearchFailed ? '查询失败' : '查询结果'}
          </h1>
          {productName && (
            <p className="text-sm text-gray-500 mb-1">
              产品：<span className="font-medium text-gray-800">{productName}</span>
            </p>
          )}
          {keyword && (
            <p className="text-sm text-gray-500 mb-1">
              关键词：<span className="font-medium text-gray-800">{keyword}</span>
            </p>
          )}
          {orderNo && (
            <p className="text-sm text-gray-500 mb-1">
              订单号：<span className="font-mono font-medium text-gray-800">{orderNo}</span>
            </p>
          )}
          {!loading && parsed.ok && parsed.rows.length > 0 && (
            <p className="text-sm text-gray-500">
              共 <span className="font-semibold text-gray-900">{parsed.totalRows}</span> 条记录
              {pageCount > 1 && (
                <span className="text-gray-400 ml-2">
                  （当前第 {rangeStart}–{rangeEnd} 条）
                </span>
              )}
            </p>
          )}
          {/* <p className="text-xs text-amber-600 mt-3 bg-amber-50 inline-block px-2.5 py-1 rounded-lg">
            部分记录字段较少，可能已脱敏处理
          </p> */}
        </div>

        {loading && (
          <div className="py-20 text-center text-gray-500 text-sm">加载结果中…</div>
        )}

        {!loading && isSearchFailed && (
          <SearchFailurePanel
            keyword={keyword}
            pointsBalance={pointsBalance}
            reason={failureReason}
            searchOrderId={currentSearchOrderId}
            onContactSupport={handleContactSupport}
          />
        )}

        {!loading && showError && (
          <div className="py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-white">
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 underline"
            >
              返回首页继续搜索
            </Link>
          </div>
        )}

        {!loading && !isSearchFailed && !showError && (
          <>
            {/* Mobile: cards */}
            <div className="lg:hidden space-y-4 mb-6">
              {pageRows.map((row, idx) => (
                <SearchResultCard
                  key={`card-${safePage}-${idx}`}
                  row={row}
                  index={rangeStart + idx}
                />
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                        #
                      </th>
                      {fieldKeys.map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, idx) => (
                      <tr key={`row-${safePage}-${idx}`} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{rangeStart + idx}</td>
                        {fieldKeys.map((key) => (
                          <td key={key} className="px-4 py-3 text-gray-800 break-all max-w-xs">
                            {formatSearchFieldValue(row[key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {pageCount > 1 && (
              <SearchPagination
                page={safePage}
                pageCount={pageCount}
                onPageChange={goToPage}
              />
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SearchFailurePanel({
  keyword,
  pointsBalance,
  reason,
  searchOrderId,
  onContactSupport,
}: {
  keyword: string;
  pointsBalance?: number;
  reason: string;
  searchOrderId?: number;
  onContactSupport: () => void;
}) {
  const rows = [
    { label: '查询内容', value: keyword || '-' },
    { label: '查询ID', value: searchOrderId ? String(searchOrderId) : '-' },
    { label: '当前积分', value: `${pointsBalance ?? '--'} 分` },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">查询失败，积分已退还</h2>
        </div>

        <div className="rounded-2xl bg-gray-50 border border-gray-100 divide-y divide-gray-100 mb-6">
          {rows.map((row) => (
            <div key={row.label} className="px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
              <div className="w-24 text-sm font-medium text-gray-500 flex-shrink-0">{row.label}：</div>
              <div className="text-sm text-gray-900 break-all">{row.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-4 sm:px-5 mb-6">
          <p className="text-sm text-gray-900">
            <span className="font-medium">失败原因：</span>
            {reason || SEARCH_FAILURE_REASON}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-gray-600">
            如有疑问请：
            <a
              href="#customer-service"
              onClick={(event) => {
                event.preventDefault();
                onContactSupport();
              }}
              className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2 cursor-pointer"
            >
              联系客服
            </a>
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-1 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            返回首页继续搜索
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SearchResultCard({ row, index }: { row: SearchResultRow; index: number }) {
  const keys = Object.keys(row).filter((k) => row[k] != null && row[k] !== '');

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="text-xs text-gray-400 font-mono mb-3">#{index}</div>
      <dl className="space-y-2">
        {keys.map((key) => (
          <div key={key} className="border-b border-gray-50 pb-2 last:border-0 last:pb-0">
            <dt className="text-xs font-medium text-gray-500 mb-0.5">{key}</dt>
            <dd className="text-sm text-gray-900 break-all">{formatSearchFieldValue(row[key])}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function SearchPagination({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (p: number) => void;
}) {
  const pages = useMemo(() => {
    const items: number[] = [];
    const maxVisible = 7;
    if (pageCount <= maxVisible) {
      for (let i = 1; i <= pageCount; i++) items.push(i);
      return items;
    }
    let start = Math.max(1, page - 3);
    let end = Math.min(pageCount, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) items.push(i);
    return items;
  }, [page, pageCount]);

  return (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50"
      >
        上一页
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange(p)}
          className={`min-w-[2.25rem] px-3 py-2 text-sm rounded-lg border ${
            p === page
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50"
      >
        下一页
      </button>
    </div>
  );
}
